import {FC, FormEvent, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {OFFER_SORT_OPTIONS} from '../cors/_models'

/**
 * Filter form for My Offers. Persists state in URL search params so
 * suppliers can bookmark / share filtered views and pagination keeps
 * the filter context.
 *
 * Filters surfaced (mapped 1:1 to backend query params):
 *   status        : active | paused | out_of_stock | unpriced | (omitted = all)
 *   buy box       : its OWN dropdown (separate from status) →
 *                   winning_only=1 | not_winning=1 | (omitted = all)
 *   search        : matches barcode prefix, supplier_sku prefix, name LIKE
 */

// Buy-box dropdown ↔ URL: 'winning' → winning_only=1, 'not_winning' →
// not_winning=1, '' → neither.
const initialBuyBox = (sp: URLSearchParams): string =>
  sp.get('winning_only') === '1' ? 'winning' : sp.get('not_winning') === '1' ? 'not_winning' : ''

type Props = {
  /** Offer the "No price" status only when the supplier has such an offer. */
  hasUnpricedOffers: boolean
}

const OffersTableFilter: FC<Props> = ({hasUnpricedOffers}) => {
  const {formatMessage} = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()

  const [status, setStatus] = useState<string>(searchParams.get('status') ?? '')
  const [buyBox, setBuyBox] = useState<string>(initialBuyBox(searchParams))
  const [search, setSearch] = useState<string>(searchParams.get('search') ?? '')
  const [sort, setSort] = useState<string>(searchParams.get('sort') ?? '')
  // Only an active offer can hold the buy box, so "Winning only" and every
  // other status exclude each other.
  const winningSelected = buyBox === 'winning'
  const winningIncompatibleStatus = status !== '' && status !== 'active'

  // Order-by applies immediately (a sort doesn't need the Apply step). apply()
  // below preserves the 'sort' param, and reset() clears it alongside the rest.
  const onSortChange = (value: string) => {
    setSort(value)
    const next = new URLSearchParams(searchParams)
    if (value) next.set('sort', value)
    else next.delete('sort')
    next.delete('current_page') // re-sort from page 1
    setSearchParams(next)
  }

  const apply = (e: FormEvent) => {
    e.preventDefault()
    const next = new URLSearchParams(searchParams)
    next.delete('status')
    next.delete('winning_only')
    next.delete('not_winning')
    next.delete('search')
    next.delete('current_page') // reset to page 1 on filter change
    if (status) next.set('status', status)
    if (buyBox === 'winning') next.set('winning_only', '1')
    else if (buyBox === 'not_winning') next.set('not_winning', '1')
    const term = search.trim()
    if (term !== '') next.set('search', term)
    setSearchParams(next)
  }

  const reset = () => {
    setStatus('')
    setBuyBox('')
    setSearch('')
    setSort('')
    const next = new URLSearchParams(searchParams)
    next.delete('status')
    next.delete('winning_only')
    next.delete('not_winning')
    next.delete('search')
    next.delete('sort')
    next.delete('current_page')
    setSearchParams(next)
  }

  return (
    <form
      className="hsb-of-filter"
      onSubmit={apply}
      role="search"
      aria-label={formatMessage({id: 'offers.filter.aria'})}
    >
      <div className="hsb-of-filter-row">
        <div className="hsb-of-field hsb-of-field--search">
          <label className="hsb-of-label">
            {formatMessage({id: 'offers.filter.search'})}
          </label>
          <div className="hsb-of-search">
            <span className="material-symbols-outlined hsb-of-search-icon">search</span>
            <input
              type="text"
              className="hsb-of-input"
              placeholder={formatMessage({id: 'offers.filter.search_placeholder'})}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxLength={64}
            />
          </div>
        </div>

        <div className="hsb-of-field">
          <label className="hsb-of-label">
            {formatMessage({id: 'offers.filter.status'})}
          </label>
          <select
            className="hsb-of-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">{formatMessage({id: 'offers.filter.status.all'})}</option>
            <option value="active">{formatMessage({id: 'offers.filter.status.active'})}</option>
            <option value="paused" disabled={winningSelected}>
              {formatMessage({id: 'offers.filter.status.paused'})}
            </option>
            <option value="out_of_stock" disabled={winningSelected}>
              {formatMessage({id: 'offers.filter.status.out_of_stock'})}
            </option>
            {hasUnpricedOffers || status === 'unpriced' ? (
              <option value="unpriced" disabled={winningSelected}>
                {formatMessage({id: 'offers.filter.status.unpriced'})}
              </option>
            ) : null}
          </select>
        </div>

        {/* Winning is its OWN dropdown (separate from the status select). */}
        <div className="hsb-of-field">
          <label className="hsb-of-label">
            {formatMessage({id: 'offers.filter.winning_label'})}
          </label>
          <select
            className="hsb-of-select"
            value={buyBox}
            onChange={(e) => setBuyBox(e.target.value)}
          >
            <option value="">{formatMessage({id: 'offers.filter.winning.all'})}</option>
            <option value="winning" disabled={winningIncompatibleStatus}>
              ★ {formatMessage({id: 'offers.filter.winning_only'})}
            </option>
            <option value="not_winning">{formatMessage({id: 'offers.filter.not_winning'})}</option>
          </select>
        </div>

        {/* Order by — applies instantly on change (independent of Apply). */}
        <div className="hsb-of-field">
          <label className="hsb-of-label">
            {formatMessage({id: 'offers.sort.label'})}
          </label>
          <select
            className="hsb-of-select"
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
          >
            {OFFER_SORT_OPTIONS.map((o) => (
              <option key={o.value || 'default'} value={o.value}>
                {formatMessage({id: o.labelKey})}
              </option>
            ))}
          </select>
        </div>

        <div className="hsb-of-actions">
          <button type="submit" className="hsb-of-btn-primary">
            {formatMessage({id: 'offers.filter.apply'})}
          </button>
          <button
            type="button"
            className="hsb-of-btn-icon"
            onClick={reset}
            aria-label={formatMessage({id: 'offers.filter.reset'})}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      </div>
    </form>
  )
}

export default OffersTableFilter
